import type { HTMLAttributes, ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, CircleDashed, Clock, Info, Loader2, OctagonAlert, RefreshCw, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { ApiError } from '@/lib/api'
import { initials } from '@/lib/format'

export function Card({ className, ...p }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...p} />
}

export function CardHeader({ title, description, actions, className, icon }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <div className={cn('flex items-start justify-between gap-3 border-b border-ink-100 px-5 py-3.5', className)}>
      <div className="flex min-w-0 items-start gap-2.5">
        {icon && <div className="mt-0.5 text-ink-500">{icon}</div>}
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-ink-900">{title}</h3>
          {description && <p className="mt-0.5 text-[13px] text-ink-500">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

export type Tone = 'neutral' | 'brand' | 'good' | 'warning' | 'serious' | 'critical' | 'info' | 'violet'

const tones: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  brand: 'bg-brand-50 text-brand-800 ring-brand-200',
  good: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  serious: 'bg-orange-50 text-orange-800 ring-orange-200',
  critical: 'bg-rose-50 text-rose-800 ring-rose-200',
  info: 'bg-sky-50 text-sky-800 ring-sky-200',
  violet: 'bg-violet-50 text-violet-800 ring-violet-200',
}

export function Badge({ tone = 'neutral', className, children, icon }: { tone?: Tone; className?: string; children: ReactNode; icon?: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset', tones[tone], className)}>
      {icon}
      {children}
    </span>
  )
}

/** Workflow states → tone + icon + label. Colour is never the only signal. */
const STATUS: Record<string, { tone: Tone; label?: string; icon?: 'done' | 'wait' | 'warn' | 'stop' | 'progress' | 'crit' }> = {
  active: { tone: 'good', icon: 'done' },
  inactive: { tone: 'neutral' },
  merged: { tone: 'neutral', label: 'Merged' },
  deceased: { tone: 'neutral' },
  waiting: { tone: 'warning', icon: 'wait' },
  called: { tone: 'info', icon: 'progress' },
  in_consultation: { tone: 'brand', label: 'With doctor', icon: 'progress' },
  completed: { tone: 'good', icon: 'done' },
  cancelled: { tone: 'neutral', icon: 'stop' },
  no_show: { tone: 'serious', label: 'No-show', icon: 'stop' },
  requested: { tone: 'violet', icon: 'wait' },
  accepted: { tone: 'brand', label: 'Accepted', icon: 'progress' },
  declined: { tone: 'serious', label: 'Declined', icon: 'stop' },
  booked: { tone: 'info' },
  checked_in: { tone: 'brand', label: 'Checked in', icon: 'done' },
  open: { tone: 'info', icon: 'wait' },
  in_progress: { tone: 'brand', label: 'In progress', icon: 'progress' },
  closed: { tone: 'neutral', icon: 'done' },
  draft: { tone: 'warning', icon: 'wait' },
  signed: { tone: 'good', icon: 'done' },
  superseded: { tone: 'neutral', label: 'Superseded' },
  ordered: { tone: 'info', icon: 'wait' },
  collected: { tone: 'violet', icon: 'progress' },
  received: { tone: 'brand', icon: 'progress' },
  resulted: { tone: 'warning', label: 'Awaiting verification', icon: 'wait' },
  verified: { tone: 'good', icon: 'done' },
  rejected: { tone: 'critical', label: 'Rejected', icon: 'stop' },
  scheduled: { tone: 'info', icon: 'wait' },
  performed: { tone: 'violet', icon: 'progress' },
  reported: { tone: 'warning', label: 'Reported', icon: 'wait' },
  partially_dispensed: { tone: 'warning', label: 'Partly dispensed' },
  dispensed: { tone: 'good', icon: 'done' },
  issued: { tone: 'warning', label: 'Unpaid', icon: 'wait' },
  partially_paid: { tone: 'serious', label: 'Part paid', icon: 'warn' },
  paid: { tone: 'good', icon: 'done' },
  void: { tone: 'neutral', label: 'Cancelled', icon: 'stop' },
  unbilled: { tone: 'warning' },
  billed: { tone: 'good' },
  admitted: { tone: 'brand', icon: 'progress' },
  discharge_planned: { tone: 'violet', label: 'Discharge planned' },
  discharged: { tone: 'neutral', icon: 'done' },
  available: { tone: 'good' },
  occupied: { tone: 'brand' },
  reserved: { tone: 'violet' },
  cleaning: { tone: 'warning' },
  maintenance: { tone: 'neutral' },
  pending: { tone: 'warning', icon: 'wait' },
  approved: { tone: 'good', icon: 'done' },
  submitted: { tone: 'info', icon: 'wait' },
  under_review: { tone: 'violet', label: 'Under review' },
  partially_approved: { tone: 'warning', label: 'Partly approved' },
  partially_received: { tone: 'warning', label: 'Partly received' },
  success: { tone: 'good', icon: 'done' },
  failed: { tone: 'critical', icon: 'crit' },
  expired: { tone: 'critical', icon: 'crit' },
  queued: { tone: 'info', icon: 'wait' },
  running: { tone: 'brand', icon: 'progress' },
  done: { tone: 'good', icon: 'done' },
  routine: { tone: 'neutral' },
  urgent: { tone: 'serious', icon: 'warn' },
  stat: { tone: 'critical', label: 'STAT', icon: 'crit' },
}

const ICONS = {
  done: <CheckCircle2 className="h-3 w-3" aria-hidden />,
  wait: <Clock className="h-3 w-3" aria-hidden />,
  warn: <AlertTriangle className="h-3 w-3" aria-hidden />,
  stop: <XCircle className="h-3 w-3" aria-hidden />,
  progress: <CircleDashed className="h-3 w-3" aria-hidden />,
  crit: <OctagonAlert className="h-3 w-3" aria-hidden />,
}

export function StatusBadge({ status, label, className }: { status: string; label?: string; className?: string }) {
  const s = STATUS[status] ?? { tone: 'neutral' as Tone }
  const text = label ?? s.label ?? status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  return (
    <Badge tone={s.tone} className={className} icon={s.icon ? ICONS[s.icon] : undefined}>
      {text}
    </Badge>
  )
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-sm text-ink-500', className)}>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-ink-100', className)} />
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-5" role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={cn('h-4', i % 3 === 2 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  )
}

export function EmptyState({ icon, title, description, action, className }: { icon?: ReactNode; title: string; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {icon && <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-ink-100 text-ink-500">{icon}</div>}
      <p className="text-sm font-semibold text-ink-800">{title}</p>
      {description && <p className="mt-1 max-w-sm text-[13px] text-ink-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const msg = error instanceof ApiError ? error.message : 'Something went wrong while loading this.'
  const forbidden = error instanceof ApiError && error.status === 403
  return (
    <div className={cn('flex flex-col items-center px-6 py-10 text-center', className)} role="alert">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold text-ink-800">{forbidden ? 'Access restricted' : 'Could not load'}</p>
      <p className="mt-1 max-w-md text-[13px] text-ink-500">{msg}</p>
      {error instanceof ApiError && error.requestId && <p className="mt-1 text-[11px] text-ink-500">Reference: {error.requestId}</p>}
      {onRetry && !forbidden && (
        <Button variant="secondary" size="sm" className="mt-4" icon={<RefreshCw className="h-3.5 w-3.5" />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

export function Callout({ tone = 'info', title, children, className }: { tone?: 'info' | 'warning' | 'critical' | 'good'; title?: ReactNode; children?: ReactNode; className?: string }) {
  const map = {
    info: ['border-sky-200 bg-sky-50 text-sky-900', <Info key="i" className="h-4 w-4 text-sky-600" />],
    warning: ['border-amber-200 bg-amber-50 text-amber-900', <AlertTriangle key="w" className="h-4 w-4 text-amber-600" />],
    critical: ['border-rose-200 bg-rose-50 text-rose-900', <OctagonAlert key="c" className="h-4 w-4 text-rose-600" />],
    good: ['border-emerald-200 bg-emerald-50 text-emerald-900', <CheckCircle2 key="g" className="h-4 w-4 text-emerald-600" />],
  } as const
  const [cls, icon] = map[tone]
  return (
    <div className={cn('flex gap-2.5 rounded-xl border px-3.5 py-3 text-[13px]', cls, className)} role={tone === 'critical' ? 'alert' : undefined}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5', 'leading-relaxed opacity-90')}>{children}</div>}
      </div>
    </div>
  )
}

export function Avatar({ name, className }: { name: string | null | undefined; className?: string }) {
  const palette = ['bg-brand-100 text-brand-800', 'bg-violet-100 text-violet-800', 'bg-amber-100 text-amber-800', 'bg-sky-100 text-sky-800', 'bg-rose-100 text-rose-800', 'bg-emerald-100 text-emerald-800']
  const idx = (name ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length
  return (
    <span aria-hidden className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold', palette[idx], className)}>
      {initials(name)}
    </span>
  )
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-ink-200 bg-ink-50 px-1.5 py-0.5 font-sans text-[11px] font-medium text-ink-500">{children}</kbd>
}

export function DefinitionList({ items, className, cols = 2 }: { items: [ReactNode, ReactNode][]; className?: string; cols?: 1 | 2 | 3 }) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-3', cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3', className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="min-w-0">
          <dt className="text-xs text-ink-500">{k}</dt>
          <dd className="mt-0.5 truncate text-sm text-ink-900">{v || <span className="text-ink-500">—</span>}</dd>
        </div>
      ))}
    </dl>
  )
}
